import { useState, useEffect, useCallback } from 'react';
import api, { openProtectedFile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, AlertTriangle, CheckCircle, Clock, Upload, Plus, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const categories = ['license', 'registration', 'insurance', 'compliance', 'property', 'staff', 'vendor'];

export default function DocumentManagement() {
  const [documents, setDocuments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [formData, setFormData] = useState({ name: '', category: 'license', authority: '', document_number: '', issue_date: '', expiry_date: '', renewal_frequency: 'annual', mandatory: true, responsible_person: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      const [docsRes, dashRes] = await Promise.all([
        api.get('/documents', { params: { category: filterCat || undefined } }),
        api.get('/documents/dashboard'),
      ]);
      setDocuments(docsRes.data);
      setDashboard(dashRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filterCat]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/documents', formData);
      toast.success('Document added');
      setModalOpen(false);
      setFormData({ name: '', category: 'license', authority: '', document_number: '', issue_date: '', expiry_date: '', renewal_frequency: 'annual', mandatory: true, responsible_person: '', notes: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document record?')) return;
    try { await api.delete(`/documents/${id}`); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const statusIcon = (status) => {
    if (status === 'expired') return <AlertTriangle className="w-4 h-4 text-red-600" />;
    if (status === 'expiring_soon') return <Clock className="w-4 h-4 text-amber-600" />;
    if (status === 'valid') return <CheckCircle className="w-4 h-4 text-emerald-600" />;
    return <FileText className="w-4 h-4 text-stone-400" />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>;

  return (
    <div data-testid="documents-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope']">Compliance & Documents</h1>
          <p className="text-stone-500 mt-1">License tracking, expiry alerts, and compliance management</p>
        </div>
        <Button onClick={() => setModalOpen(true)} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="add-document-btn">
          <Plus className="w-4 h-4 mr-2" />Add Document
        </Button>
      </div>

      {/* Compliance Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-emerald-50 text-emerald-700 rounded-md p-3 text-center">
            <p className="text-2xl font-bold font-['Manrope']">{dashboard.valid}</p>
            <p className="text-xs">Valid</p>
          </div>
          <div className="bg-amber-50 text-amber-700 rounded-md p-3 text-center">
            <p className="text-2xl font-bold font-['Manrope']">{dashboard.expiring_30_days}</p>
            <p className="text-xs">Expiring (30d)</p>
          </div>
          <div className="bg-sage-50 text-sage-700 rounded-md p-3 text-center">
            <p className="text-2xl font-bold font-['Manrope']">{dashboard.expiring_90_days}</p>
            <p className="text-xs">Expiring (90d)</p>
          </div>
          <div className="bg-red-50 text-red-700 rounded-md p-3 text-center">
            <p className="text-2xl font-bold font-['Manrope']">{dashboard.expired}</p>
            <p className="text-xs">Expired</p>
          </div>
          <div className="bg-stone-50 text-stone-600 rounded-md p-3 text-center">
            <p className="text-2xl font-bold font-['Manrope']">{dashboard.not_uploaded}</p>
            <p className="text-xs">Pending Upload</p>
          </div>
        </div>
      )}

      {/* Alerts */}
      {dashboard?.alerts?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-2"><Shield className="w-4 h-4" />Compliance Alerts</h3>
          <div className="space-y-1">
            {dashboard.alerts.slice(0, 5).map(a => (
              <p key={a.id} className="text-sm text-red-700">
                {a.document}: {a.days < 0 ? `Expired ${Math.abs(a.days)} days ago` : `Expires in ${a.days} days`}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="mb-4">
        <Select value={filterCat || 'all'} onValueChange={v => setFilterCat(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 border-stone-300"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      <div className="space-y-2">
        {documents.map(doc => (
          <div key={doc.id} className="bg-white border border-stone-200 rounded-md p-4 flex items-center gap-4" data-testid={`doc-${doc.id}`}>
            {statusIcon(doc.status)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-stone-900">{doc.name}</p>
                {doc.mandatory && <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-600 rounded">Required</span>}
                <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded capitalize">{doc.category}</span>
              </div>
              <p className="text-sm text-stone-500">{doc.authority || 'No authority specified'} {doc.document_number ? `• ${doc.document_number}` : ''}</p>
              {doc.expiry_date && <p className="text-xs text-stone-400">Expires: {doc.expiry_date} • {doc.renewal_frequency}</p>}
            </div>
            <div className="flex items-center gap-2">
              {doc.file_url ? (
                <button type="button" onClick={() => openProtectedFile(doc.file_url).catch(() => toast.error('Could not open file'))} className="text-xs text-sage-700 hover:underline flex items-center gap-1" data-testid={`view-doc-file-${doc.id}`}><FileText className="w-3 h-3" />View</button>
              ) : (
                <span className="text-xs text-stone-400">No file</span>
              )}
              <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} className="h-8 w-8 p-0 text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Manrope']">Add Document/License</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="border-stone-300" placeholder="Trade License" /></div>
              <div className="space-y-1"><Label>Category</Label>
                <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Authority</Label><Input value={formData.authority} onChange={e => setFormData({...formData, authority: e.target.value})} className="border-stone-300" placeholder="Municipal Corporation" /></div>
              <div className="space-y-1"><Label>Document Number</Label><Input value={formData.document_number} onChange={e => setFormData({...formData, document_number: e.target.value})} className="border-stone-300" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Issue Date</Label><Input type="date" value={formData.issue_date} onChange={e => setFormData({...formData, issue_date: e.target.value})} className="border-stone-300" /></div>
              <div className="space-y-1"><Label>Expiry Date</Label><Input type="date" value={formData.expiry_date} onChange={e => setFormData({...formData, expiry_date: e.target.value})} className="border-stone-300" /></div>
              <div className="space-y-1"><Label>Renewal</Label>
                <Select value={formData.renewal_frequency} onValueChange={v => setFormData({...formData, renewal_frequency: v})}>
                  <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="annual">Annual</SelectItem><SelectItem value="biennial">Biennial</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="one-time">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white">Add Document</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
